import { PieChart, Pie, Cell } from "recharts";
import { MACROS, formatRange, quantityLabel } from "@/data/milletNutrition";

export const MealPrep = () => (
  <section id="meal-prep" className="grid scroll-mt-6 gap-10 border-y border-cream-muted py-12 sm:py-16 lg:grid-cols-2 lg:gap-16" aria-label="Meal prep and macro distribution">
    <div>
      <p className="mb-2 font-mono text-xs uppercase text-terracotta">02 / Your daily portion</p>
      <h2 className="font-display text-base font-semibold md:text-lg" data-testid="meal-prep-title">A little planning. A nourishing routine.</h2>
      <p className="mt-5 text-sm font-semibold" data-testid="meal-prep-serving">One reference serving: 35 g dry powder</p>
      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="meal-prep-metrics">
        {[["energy", "Calories"], ["protein", "Protein"], ["carbs", "Carbs"]].map(([id, label]) => (
          <div key={id} className="border-l-2 border-saffron pl-3">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-sm font-semibold tabular-nums" data-testid={`reference-serving-${id}`}>{formatRange(id, 35)}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-8 divide-y divide-cream-muted">
        {[250, 500, 1000].map((grams) => (
          <div key={grams} className="flex flex-wrap items-center justify-between gap-2 py-4" data-testid={`pack-yield-${grams}`}>
            <span className="text-sm font-semibold">{quantityLabel(grams)} pack</span>
            <span className="text-sm">{Math.floor(grams / 35)} full servings <span className="text-xs text-muted-foreground">+ {grams % 35} g left</span></span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground" data-testid="meal-prep-pack-note">Yields assume a 35 g serving. Quantities shown are planning references, not additional pack options for sale.</p>
    </div>
    <figure className="min-w-0" data-testid="macro-distribution">
      <figcaption className="font-display text-base font-semibold md:text-lg">Where the energy comes from</figcaption>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-5 sm:flex-nowrap">
        <div className="relative h-[208px] w-[208px] shrink-0" role="img" aria-label={MACROS.map((item) => `${item.name}: ${item.percent}% of estimated macro energy`).join(", ")} data-testid="macro-chart">
            <PieChart width={208} height={208} accessibilityLayer={false}>
              <Pie data={MACROS} dataKey="value" innerRadius="65%" outerRadius="95%" startAngle={90} endAngle={-270} stroke="none" paddingAngle={2} isAnimationActive={false}>
                {MACROS.map((macro) => <Cell key={macro.id} fill={macro.color} />)}
              </Pie>
            </PieChart>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
            <span className="font-display text-3xl font-semibold">100%</span>
            <span className="text-xs text-muted-foreground">macro energy</span>
          </div>
        </div>
        <dl className="w-full space-y-4 sm:w-auto sm:flex-1">
          {MACROS.map((macro) => (
            <div key={macro.id} className="flex items-center justify-between gap-4 text-sm" data-testid={`macro-legend-${macro.id}`}>
              <dt className="flex items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: macro.color }} aria-hidden="true" />{macro.name}</dt>
              <dd className="font-semibold tabular-nums">{macro.percent}%</dd>
            </div>
          ))}
        </dl>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground" data-testid="macro-method-note">Approximate energy shares, not percentages by weight. Uses the midpoint of each 100 g macro range and 4 kcal/g for carbohydrate and protein, 9 kcal/g for fat. Fibre is not counted separately. This simplified estimate may differ from the stated calorie range.</p>
    </figure>
  </section>
);